import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { createReminder } from 'store/slices/remindersSlice';
import { Wizard } from 'components/layout/wizards';
import {
  REMINDER_TYPES,
  REMINDER_FREQUENCIES,
  REMINDER_RANGE_SUPPORTED_FREQUENCIES,
} from 'constants/reminders';
import { selectSettings } from 'store/slices/authSlice';
import { hasSeasonalRange } from 'utils/reminders';
import ReminderTypeStep from './steps/ReminderTypeStep/ReminderTypeStep';
import OneTimeDateStep from './steps/OneTimeDateStep/OneTimeDateStep';
import RepeatingFrequencyStep from './steps/RepeatingFrequencyStep/RepeatingFrequencyStep';
import RepeatingScheduleStep from './steps/RepeatingScheduleStep/RepeatingScheduleStep';
import DetailsStep from './steps/DetailsStep/DetailsStep';
import ReviewStep from './steps/ReviewStep/ReviewStep';

const ADD_REMINDER_STEPS = {
  TYPE: { id: 'type', component: ReminderTypeStep },
  ONE_TIME_DATE: { id: 'date', component: OneTimeDateStep },
  REPEATING_FREQUENCY: { id: 'frequency', component: RepeatingFrequencyStep },
  REPEATING_SCHEDULE: { id: 'schedule', component: RepeatingScheduleStep },
  DETAILS: { id: 'details', component: DetailsStep },
  REVIEW: { id: 'review', component: ReviewStep },
}

const getSteps = (data) => {
  const baseSteps = [ADD_REMINDER_STEPS.TYPE];

  if (data.type === REMINDER_TYPES.ONE_TIME) {
    baseSteps.push(ADD_REMINDER_STEPS.ONE_TIME_DATE);
    baseSteps.push(ADD_REMINDER_STEPS.DETAILS);
    baseSteps.push(ADD_REMINDER_STEPS.REVIEW);
  } else if (data.type === REMINDER_TYPES.REPEATING) {
    baseSteps.push(ADD_REMINDER_STEPS.REPEATING_FREQUENCY);
    baseSteps.push(ADD_REMINDER_STEPS.REPEATING_SCHEDULE);
    baseSteps.push(ADD_REMINDER_STEPS.DETAILS);
    baseSteps.push(ADD_REMINDER_STEPS.REVIEW);
  } else {
    // Default view: Show 4 steps (Type + 3 placeholders) to indicate progress
    baseSteps.push(ADD_REMINDER_STEPS.ONE_TIME_DATE);
    baseSteps.push(ADD_REMINDER_STEPS.DETAILS);
    baseSteps.push(ADD_REMINDER_STEPS.REVIEW);
  }

  return baseSteps;
};

export default function AddReminderWizard() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const settings = useSelector(selectSettings);
  const [searchParams, setSearchParams] = useSearchParams();
  const [wizardData, setWizardData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);

  const steps = useMemo(() => getSteps(wizardData), [wizardData]);

  const currentStepId = searchParams.get('step') || 'type';
  const currentStepIndex = useMemo(() => {
    const index = steps.findIndex(s => s.id === currentStepId);
    return index >= 0 ? index : 0;
  }, [steps, currentStepId]);

  // Sync URL if step is invalid or missing
  useEffect(() => {
    if (currentStepIndex === 0 && currentStepId !== 'type') {
      setSearchParams({ step: 'type' }, { replace: true });
    }

    // Prevent jumping ahead without required data
    if (currentStepIndex > 0) {
      if (!wizardData.type) {
        setSearchParams({ step: 'type' }, { replace: true });
        return;
      }

      if (wizardData.type === REMINDER_TYPES.REPEATING) {
        // If we are at schedule (index 2) or details (index 3), we need frequency
        // Frequency is at index 1.
        if (currentStepIndex > 1 && !wizardData.frequency) {
          setSearchParams({ step: 'frequency' }, { replace: true });
        }
      }
    }
  }, [currentStepIndex, currentStepId, setSearchParams, wizardData]);

  const handleClose = () => {
    navigate('/reminders');
  };

  const handleStepChange = (nextIndex, newData) => {
    // Calculate what the steps WOULD be with the new data
    const nextSteps = getSteps(newData);

    if (nextIndex < nextSteps.length) {
      const nextStepId = nextSteps[nextIndex].id;
      setSearchParams({ step: nextStepId });
    } else {
      handleComplete(newData);
    }
  };

  const handleComplete = async (data) => {
    if (isSubmittingRef.current) {
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      const schedule = data.schedule || {};
      // Construct clean payload
      const payload = {
        type: data.type,
        subject: data.subject,
        message: data.message,
        schedule: {
          timezone: schedule?.timezone || settings?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
        }
      };

      if (data.type === REMINDER_TYPES.ONE_TIME) {
        payload.schedule.executionDate = schedule.executionDate;
      } else if (data.type === REMINDER_TYPES.REPEATING) {
        payload.frequency = data.frequency;

        if (data.frequency === REMINDER_FREQUENCIES.DAILY) {
          payload.schedule.time = schedule.time;
        } else if (data.frequency === REMINDER_FREQUENCIES.WEEKLY) {
          payload.schedule.time = schedule.time;
          payload.schedule.daysOfWeek = schedule.daysOfWeek;
        } else if (data.frequency === REMINDER_FREQUENCIES.MONTHLY) {
          payload.schedule.time = schedule.time;
          payload.schedule.daysOfMonth = schedule.daysOfMonth;
        } else if (data.frequency === REMINDER_FREQUENCIES.ANNUAL) {
          payload.schedule.executionDate = schedule.executionDate;
        }

        if (
          REMINDER_RANGE_SUPPORTED_FREQUENCIES.includes(data.frequency)
          && hasSeasonalRange(schedule)
        ) {
          payload.schedule.rangeStartMonth = schedule.rangeStartMonth;
          payload.schedule.rangeStartDay = schedule.rangeStartDay;
          payload.schedule.rangeEndMonth = schedule.rangeEndMonth;
          payload.schedule.rangeEndDay = schedule.rangeEndDay;
        }
      }

      await dispatch(createReminder(payload)).unwrap();
      navigate('/reminders');
    } catch (error) {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
      console.error('Failed to create reminder:', error);
    }
  };

  return (
    <Wizard
      title="Create Reminder"
      steps={steps}
      currentStepIndex={currentStepIndex}
      onClose={handleClose}
      onStepChange={handleStepChange}
      onDataChange={setWizardData}
      initialData={{ ...wizardData, isSubmitting }}
    />
  );
}
